// Public Support Portal Layout - White-labeled for each organization
import { useState, useEffect } from 'react';
import { Outlet, useParams, Link, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Container,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Paper,
  alpha,
  useTheme,
} from '@mui/material';
import { Search, Home, Article, Lightbulb, Map } from '@mui/icons-material';
import { organizationsService } from '@/services/collections/organizations';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import type { Organization, Project, BrandingSettings } from '@/types';
import { DEFAULT_BRANDING } from '@/types';

interface PortalContextValue {
  organization: Organization | null;
  project: Project | null;
  branding: BrandingSettings;
}

export const PortalLayout = () => {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const location = useLocation();
  useTheme();

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch organization by slug
  useEffect(() => {
    const fetchOrg = async () => {
      if (!orgSlug) return;

      setLoading(true);
      setError(null);

      try {
        const org = await organizationsService.getBySlug(orgSlug);
        if (!org) {
          setError('Organization not found');
          return;
        }

        setOrganization(org);

        // Fetch first project
        const projects = await organizationsService.getProjects(org.id);
        if (projects.length > 0) {
          setProject(projects[0]);
        }
      } catch (err) {
        console.error('Error fetching organization:', err);
        setError('Failed to load support portal');
      } finally {
        setLoading(false);
      }
    };

    fetchOrg();
  }, [orgSlug]);

  // Get branding (project overrides org)
  const branding: BrandingSettings = {
    ...DEFAULT_BRANDING,
    ...organization?.branding,
    ...project?.branding,
  };

  // Determine current tab using route identifiers to handle conditional tabs
  const getTabValue = () => {
    const path = location.pathname;
    if (path.includes('/kb') || path.includes('/articles')) return 'kb';
    if (path.includes('/ideas') || path.includes('/features')) return 'ideas';
    if (path.includes('/roadmap')) return 'roadmap';
    // Home tab for root, submit, and ticket pages
    return 'home';
  };

  if (loading) {
    return <LoadingSpinner fullPage message="Loading support portal..." />;
  }

  if (error || !organization) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography variant="h4" color="text.secondary">
          {error || 'Portal not found'}
        </Typography>
        <Typography color="text.secondary">
          The support portal you're looking for doesn't exist.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: branding.backgroundColor || '#f8fafc',
        fontFamily: branding.fontFamily,
      }}
    >
      {/* Custom CSS injection */}
      {branding.customCss && <style>{branding.customCss}</style>}

      {/* Header */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: branding.primaryColor,
          borderBottom: '1px solid',
          borderColor: alpha('#000', 0.1),
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ py: 1 }}>
            {/* Logo */}
            <Box
              component={Link}
              to={`/p/${orgSlug}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                textDecoration: 'none',
                color: 'inherit',
              }}
            >
              {branding.logoUrl ? (
                <Box
                  component="img"
                  src={branding.logoUrl}
                  alt={branding.logoAltText || organization.name}
                  sx={{ height: 36, width: 'auto' }}
                />
              ) : (
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: 18,
                  }}
                >
                  {organization.name[0]}
                </Box>
              )}
              <Typography variant="h6" fontWeight="bold" sx={{ color: 'white' }}>
                {branding.portalTitle || `${organization.name} Help Center`}
              </Typography>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: branding.primaryColor,
          color: 'white',
          pt: 6,
          pb: 8,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" fontWeight="bold" gutterBottom>
            {branding.welcomeMessage || 'How can we help you today?'}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, mb: 4 }}>
            {branding.portalDescription || 'Search our knowledge base or submit a request'}
          </Typography>

          {/* Search Bar */}
          <Paper
            sx={{
              display: 'flex',
              alignItems: 'center',
              maxWidth: 600,
              mx: 'auto',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <TextField
              fullWidth
              placeholder="Search for help articles..."
              variant="standard"
              InputProps={{
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position="start" sx={{ pl: 2 }}>
                    <Search />
                  </InputAdornment>
                ),
                sx: { py: 1.5, px: 1 },
              }}
            />
          </Paper>
        </Container>
      </Box>

      {/* Navigation Tabs */}
      <Box sx={{ bgcolor: 'white', borderBottom: 1, borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Tabs
            value={getTabValue()}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                minHeight: 56,
              },
            }}
          >
            <Tab
              value="home"
              icon={<Home />}
              iconPosition="start"
              label="Home"
              component={Link}
              to={`/p/${orgSlug}`}
            />
            {project?.features.knowledgeBaseEnabled && (
              <Tab
                value="kb"
                icon={<Article />}
                iconPosition="start"
                label="Knowledge Base"
                component={Link}
                to={`/p/${orgSlug}/kb`}
              />
            )}
            {project?.features.featureRequestsEnabled && (
              <Tab
                value="ideas"
                icon={<Lightbulb />}
                iconPosition="start"
                label="Feature Ideas"
                component={Link}
                to={`/p/${orgSlug}/ideas`}
              />
            )}
            {project?.features.featureRequestsEnabled && (
              <Tab
                value="roadmap"
                icon={<Map />}
                iconPosition="start"
                label="Roadmap"
                component={Link}
                to={`/p/${orgSlug}/roadmap`}
              />
            )}
          </Tabs>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Outlet context={{ organization, project, branding }} />
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 3,
          textAlign: 'center',
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'white',
        }}
      >
        {branding.footerText && (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {branding.footerText}
          </Typography>
        )}
        {branding.showPoweredBy && (
          <Typography variant="caption" color="text.secondary">
            Powered by{' '}
            <Box
              component="a"
              href="https://supportspace.io"
              target="_blank"
              sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              SupportSpace
            </Box>
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// Hook to access portal context
import { useOutletContext } from 'react-router-dom';

export const usePortal = (): PortalContextValue => {
  return useOutletContext<PortalContextValue>();
};
